// #75 FilterBar: one wrapping row over a list (DC-155) — a search field, a date select with presets, the active
// filters as chips (*Label: value* with a remove button; none → one "All" chip), *Saved views* with *Save view*,
// *Group by* and *Sort*, and **Export** as an icon button opening a menu (export formats, the security-trimmed
// drill-down). Every control is a native control or a shared primitive, so each is its own tab stop with its own name;
// the Export menu portals into the nearest layer like every menu (`preview` draws it open in place).

import { useId, type ReactNode } from "react";
import { Button } from "../core/Button";
import { Chip, Chips } from "../core/Chip";
import { Input } from "../core/Field";
import { MenuButton, type MenuItem } from "../core/Menu";
import { Select, type SelectOption } from "../core/Select";
import { ACTION_LABELS } from "../labels";
import { cx } from "../lib/cx";
import { FILTERBAR_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

/** A select in the bar: its options (his words or configured values), the chosen value and the change. */
export interface FilterBarSelect {
  options: readonly SelectOption[];
  value?: string | undefined;
  defaultValue?: string | undefined;
  onChange?: ((value: string) => void) | undefined;
  hs?: Hs | undefined;
}

/** An active filter: his filter name (§7.2) and the chosen value. */
export interface FilterBarFilter {
  name: string;
  value: string;
  onRemove?: (() => void) | undefined;
  hs?: Hs | undefined;
}

export interface FilterBarProps {
  /** Names the bar (a toolbar-like group of filters for the list it sits over). */
  label: string;
  search?: { value?: string | undefined; defaultValue?: string | undefined; onChange?: ((value: string) => void) | undefined; hs?: Hs | undefined } | undefined;
  /** The date select's presets. */
  date?: FilterBarSelect | undefined;
  /** The active filters; empty: one "All" chip. */
  filters?: readonly FilterBarFilter[] | undefined;
  savedViews?: (FilterBarSelect & { onSave?: (() => void) | undefined; saveHs?: Hs | undefined }) | undefined;
  groupBy?: FilterBarSelect | undefined;
  sort?: FilterBarSelect | undefined;
  /** The Export menu's items (export formats, the drill-down); absent: no Export. */
  exportItems?: readonly MenuItem[] | undefined;
  exportHs?: Hs | undefined;
  /** The Export menu starts open (a gallery cell). */
  exportOpen?: boolean | undefined;
  /** A gallery section's static rendering of the open menu. */
  preview?: boolean | undefined;
  /** false: the seat may not filter this list; nothing renders. */
  can?: boolean | undefined;
  className?: string | undefined;
}

function Labelled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="mc-fbar-l">
      <span className="mc-fbar-lt">{label}</span>
      {children}
    </label>
  );
}

function selectProps(select: FilterBarSelect) {
  return {
    options: select.options,
    value: select.value,
    defaultValue: select.defaultValue,
    onChange: select.onChange ? (value: string) => select.onChange?.(value) : undefined,
    hs: select.hs,
    sm: true,
  };
}

export function FilterBar({
  label, search, date, filters, savedViews, groupBy, sort, exportItems, exportHs, exportOpen, preview, can, className,
}: FilterBarProps) {
  const id = useId();
  if (can === false) return null;
  const active = filters ?? [];
  return (
    <div className={cx("mc-fbar", className)} role="group" aria-labelledby={`${id}-l`}>
      <span className="mc-sr" id={`${id}-l`}>{label}</span>
      {search ? (
        <Input
          sm
          type="search"
          icon="search"
          aria-label={FILTERBAR_LABELS.search}
          placeholder={FILTERBAR_LABELS.search}
          value={search.value}
          defaultValue={search.defaultValue}
          onChange={search.onChange ? (event) => search.onChange?.(event.currentTarget.value) : undefined}
          hs={search.hs}
          className="mc-fbar-search"
        />
      ) : null}
      {date ? <Select {...selectProps(date)} aria-label={FILTERBAR_LABELS.date} className="mc-fbar-sel" /> : null}
      <Chips className="mc-fbar-chips">
        {active.length === 0
          ? <Chip sm on>{FILTERBAR_LABELS.all}</Chip>
          : active.map((filter) => (
            <Chip key={`${filter.name}-${filter.value}`} sm name={filter.name} onRemove={filter.onRemove} hs={filter.hs}>{filter.value}</Chip>
          ))}
      </Chips>
      {savedViews ? (
        <span className="mc-fbar-views">
          <Labelled label={FILTERBAR_LABELS.savedViews}><Select {...selectProps(savedViews)} className="mc-fbar-sel" /></Labelled>
          {savedViews.onSave ? <Button sm tone="ghost" onClick={savedViews.onSave} hs={savedViews.saveHs}>{ACTION_LABELS.saveView}</Button> : null}
        </span>
      ) : null}
      {groupBy ? <Labelled label={FILTERBAR_LABELS.groupBy}><Select {...selectProps(groupBy)} className="mc-fbar-sel" /></Labelled> : null}
      {sort ? <Labelled label={FILTERBAR_LABELS.sort}><Select {...selectProps(sort)} className="mc-fbar-sel" /></Labelled> : null}
      {exportItems && exportItems.length > 0 ? (
        <MenuButton
          label={ACTION_LABELS.export}
          iconOnly
          icon="upload"
          items={exportItems}
          align="end"
          defaultOpen={exportOpen}
          preview={preview}
          hs={exportHs}
          className="mc-fbar-export"
        />
      ) : null}
    </div>
  );
}
